import { useState } from "react";
import { Plus, Ticket, TrendingDown, Eye, Lock, AlertTriangle, ArrowLeftRight } from "lucide-react";
import { ITEM_ICON, formatTime } from "@/components/trips/itinerary/itinerary-utils";
import type { PlanLeg, PlanLegItem, PlanTraveler, RebookAdvice, RebookAlternative } from "@/lib/types";
import FocusFlightCard from "./FocusFlightCard";
import LegFaresPanel from "./LegFaresPanel";
import BookingBadge from "./BookingBadge";
import DateFlexPanel from "./DateFlexPanel";
import BookTripSheet from "./BookTripSheet";
import RouteDateEditor from "./RouteDateEditor";
import RouteRecheckButton from "./RouteRecheckButton";
import CancellationCountdown from "./CancellationCountdown";

interface PlanLegSectionProps {
  tripId: string;
  leg: PlanLeg;
  /** Party size — the denominator when a paid price is a party total. */
  passengers?: number | null;
  /** Travellers by name, shown on the booking confirm step (B5). */
  travelers?: PlanTraveler[];
  /** Open the add-item dialog prefilled for this leg (date + city). */
  onAddStop: (opts: { date: string | null; city: string | null }) => void;
}

const ROLE_TAG: Record<PlanLeg["role"], string> = {
  outbound: "Outbound",
  return: "Return",
  extra: "Add-on",
};

/** Compact row for an itinerary item already attached to this leg. */
function ItemRow({ item }: { item: PlanLegItem }) {
  const Icon = ITEM_ICON[item.item_type] ?? ITEM_ICON.note;
  const time = formatTime(item.start_time);
  const where = item.location || item.city;
  return (
    <li className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--primary))]/[0.1] text-[hsl(var(--accent-foreground))]">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
        {item.title}
      </span>
      {where && (
        <span className="hidden truncate text-xs text-muted-foreground sm:inline">
          {where}
        </span>
      )}
      {time && (
        <span className="shrink-0 font-mono tabular-nums text-xs text-muted-foreground">
          {time}
        </span>
      )}
    </li>
  );
}

export default function PlanLegSection({
  tripId,
  leg,
  passengers,
  travelers,
  onAddStop,
}: PlanLegSectionProps) {
  const [bookOpen, setBookOpen] = useState(false);
  const tag = ROLE_TAG[leg.role] ?? "Leg";
  const route =
    (leg.originCity || leg.origin || "—") + " → " + (leg.destinationCity || leg.destination || "—");

  // Per-leg booking is the quiet secondary path (the primary is the trip-level
  // "Book trip" button). Offered on any not-yet-booked flight.
  const canBook = (leg.bookingStatus ?? "none") === "none";

  /*
   * On a return leg the addable city is where you START, not the home airport
   * you land at. The backend now sends that in `addStopCity`; this fallback
   * chain has to prefer origin for a return too, or a plan built before that
   * fix offers the traveler their own home town.
   */
  const addCity =
    leg.addStopCity ||
    (leg.role === "return"
      ? leg.originCity || leg.origin
      : leg.destinationCity || leg.destination);
  /*
   * NAME THE CITY on a return leg too. "Add something before you head home" was
   * true and useless: a traveler with two days in Anchorage before an evening
   * flight did not read it as the place to put an Anchorage hotel, and reported
   * that the button did not exist. The city IS the signal.
   */
  const addLabel = !addCity
    ? "Add a hotel, dining or activity"
    : leg.role === "return"
      ? "Add a hotel, dining or activity in " + addCity + " before you head home"
      : "Add a hotel, dining or activity in " + addCity;

  return (
    <section id={"leg-" + leg.routeId} className="rounded-xl border bg-card p-4">
      {/* Tag + weekday date, with booking status on the right */}
      <div className="flex items-center gap-2">
        <span className="rounded bg-[hsl(var(--primary))]/[0.12] px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--accent-foreground))]">
          {tag}
        </span>
        <RouteDateEditor
          tripId={tripId}
          routeId={leg.routeId}
          date={leg.date}
          dateLabel={leg.dateLabel}
          editable={(leg.bookingStatus ?? "none") === "none"}
        />
        <span className="ml-auto flex items-center gap-2">
          <RouteRecheckButton tripId={tripId} routeId={leg.routeId} />
          <BookingBadge
            status={leg.bookingStatus}
            holdExpiresAt={leg.bookingHoldExpiresAt}
            pricePaidPerPerson={leg.bookingPaidPerPerson}
            pricePaidTotal={leg.bookingPaidTotal}
          />
        </span>
      </div>

      {/* Full-city route headline */}
      <h3 className="mt-1 text-xl font-bold tracking-tight text-foreground">{route}</h3>

      {/* Focus flight + its detailed price-history strip (single location). */}
      <FocusFlightCard
        focus={leg.focus}
        verdict={leg.verdict}
        history={leg.history}
        originCode={leg.origin}
        destinationCode={leg.destination}
      />

      {/* What is on record for a booked/held leg: which departure, what it cost,
          and — when we don't hold a flight row for it — the fact that our fare
          watch is describing a different departure than the one you hold. */}
      {leg.bookingStatus && leg.bookingStatus !== "none" && (
        <BookedFlightNote leg={leg} passengers={passengers} />
      )}

      {/* Book just this flight — a quiet secondary link. The primary action is the
          trip-level "Book trip" button; this scopes the same sheet to one leg. */}
      {canBook && (
        <button
          type="button"
          onClick={() => setBookOpen(true)}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] transition-opacity hover:opacity-80"
        >
          <Ticket className="h-3.5 w-3.5" aria-hidden="true" />
          Book just this flight
        </button>
      )}
      <BookTripSheet
        open={bookOpen}
        onOpenChange={setBookOpen}
        tripId={tripId}
        legs={[leg]}
        passengers={passengers}
        travelers={travelers}
      />

      {/* Booked — keep watching the fare and, if a drop clears the bar, nudge a
          cancel/rebook. Honest about travel-credit + Basic-Economy lockout. */}
      {/* The free-cancellation clock sits ABOVE the rebook nudge: it expires,
          and while it is open every other option on this leg is free to take. */}
      {leg.bookingStatus === "booked" && leg.cancellationWindow && (
        <CancellationCountdown cancellation={leg.cancellationWindow} />
      )}
      {leg.bookingStatus === "booked" && leg.rebook && (
        <RebookNudge rebook={leg.rebook} onRebook={() => setBookOpen(true)} />
      )}

      {/* B13 — and what the same flight costs on the day either side, with the
          days the itinerary forbids said out loud rather than omitted. */}
      {leg.bookingStatus === "booked" && <DateFlexPanel flex={leg.dateFlex} />}

      {/* Every fare surface behind ONE pulldown, closed by default. The grid
          used to sit here permanently open and it dominated the leg; the
          itinerary below is the thing that should be tallest. */}
      <LegFaresPanel tripId={tripId} leg={leg} />

      {/* Existing itinerary items for this leg */}
      {leg.items && leg.items.length > 0 && (
        <ul className="mt-2 space-y-0.5 rounded-lg border bg-muted/20 p-1">
          {leg.items.map((it) => (
            <ItemRow key={it.id} item={it} />
          ))}
        </ul>
      )}

      {/* Inline add-stop */}
      <button
        type="button"
        onClick={() => onAddStop({ date: leg.date, city: addCity })}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[hsl(var(--primary))]/40 px-3 py-2 text-sm font-medium text-[hsl(var(--accent-foreground))] transition-colors hover:bg-[hsl(var(--primary))]/[0.06]"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {addLabel}
      </button>
    </section>
  );
}

/** What we hold on record for a booked leg (B4). Says the departure out loud,
 *  because the whole class of bug this closes is the page describing a
 *  DIFFERENT flight than the one the traveller bought.
 *
 *  It also RECONCILES the two money figures on screen. The trip total counts a
 *  booked leg at what was PAID (R1 × B4), while the flight card above shows
 *  today's fare — so on 2026-08-24 Wade read $489 out + $918 back against a
 *  $2,684 total and correctly said the arithmetic did not work. Both numbers
 *  were right; nothing on the page said they were different facts. Now it does.
 */
function BookedFlightNote({ leg, passengers }: { leg: PlanLeg; passengers?: number | null }) {
  const money = (n: number) => "$" + Math.round(n).toLocaleString();
  const bits: string[] = [];
  if (leg.bookingFlightLabel) bits.push(leg.bookingFlightLabel);
  if (leg.bookingPaidPerPerson != null) bits.push(money(leg.bookingPaidPerPerson) + " per person");
  if (leg.bookingPaidTotal != null && leg.bookingPaidTotal !== leg.bookingPaidPerPerson) {
    bits.push(money(leg.bookingPaidTotal) + " total");
  }
  if (leg.bookingRef) bits.push("confirmation " + leg.bookingRef.toUpperCase());
  const untracked = leg.bookingFlightTracked === false;

  // The reconciliation line. Only drawn when we actually hold BOTH facts for the
  // SAME departure — a live fare for another flight would restate the very
  // confusion this is here to remove.
  const paidPP = leg.bookingPaidPerPerson ?? null;
  const livePP = leg.bookingFlightTracked && leg.focus?.price != null ? leg.focus.price : null;
  const pax = passengers && passengers > 0 ? passengers : null;
  const legTotal = leg.bookingPaidTotal ?? (paidPP != null && pax ? paidPP * pax : null);
  const gap = paidPP != null && livePP != null ? Math.round(livePP - paidPP) : null;

  if (bits.length === 0 && !untracked) return null;

  return (
    <div className="mt-2 space-y-1">
      {bits.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {/* T1 — a paid price is sourced from the traveller, not scraped. Say so. */}
          <span className="font-medium text-foreground">On record:</span> {bits.join(" · ")}
          <span className="ml-1 text-[11px]">(you told us)</span>
        </div>
      )}
      {legTotal != null && (
        <div className="text-xs text-muted-foreground">
          Counts as <span className="font-mono text-foreground">{money(legTotal)}</span> in your trip
          total.
          {livePP != null && gap != null && (
            <>
              {" "}
              The <span className="font-mono">{money(livePP)}</span> above is today&apos;s fare for
              the same flight —{" "}
              {gap === 0 ? (
                "the same as you paid"
              ) : (
                <span className={gap > 0 ? "text-amber-700" : "text-emerald-700"}>
                  {money(Math.abs(gap))} {gap > 0 ? "more" : "less"} than you paid
                </span>
              )}
              .
            </>
          )}
        </div>
      )}
      {untracked && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            We aren&apos;t tracking that exact departure yet, so the fares below describe other
            flights on this route — not the one you hold.
          </span>
        </div>
      )}
    </div>
  );
}

/** "6:12 AM Delta DL1234, 1 stop" — enough to know WHICH flight is meant. */
function describeAlternative(alt: RebookAlternative): string {
  const bits: string[] = [];
  const t = formatTime(alt.departLocal);
  if (t) bits.push(t);
  if (alt.airline) bits.push(alt.airline);
  if (alt.flightNumber) bits.push(alt.flightNumber);
  const who = bits.join(" ");
  const stops =
    alt.stops == null ? null : alt.stops === 0 ? "nonstop" : alt.stops + " stop" + (alt.stops === 1 ? "" : "s");
  return [who || "another flight", stops].filter(Boolean).join(", ");
}

/**
 * Post-booking fare watch. Only ever proposes — never cancels.
 *
 * ⚠️ TWO DIFFERENT FACTS, never merged. "Your fare dropped" is only true of the
 * flight you hold; a cheaper seat on a different departure is a SWITCH, and
 * saying so is the difference between a real number and an invented one. On
 * 2026-08-24 this box read "Fare dropped $597 below what you paid" beside a
 * "Paid $918" badge and a "$918" fare — the $597 was a Delta red-eye.
 */
function RebookNudge({ rebook, onRebook }: { rebook: RebookAdvice; onRebook: () => void }) {
  const alt = rebook.alternative ?? null;

  // 1. The fare on YOUR flight actually fell far enough to act on.
  if (rebook.worthRebooking) {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-emerald-900">
              Your own flight is ${rebook.savings} cheaper than you paid
            </div>
            <div className="mt-0.5 text-xs text-emerald-800">
              Same flight, same day: you paid ${rebook.pricePaid}, it is ${rebook.currentFare} now.
              Cancelling and rebooking could save ${rebook.savings} per person. {rebook.creditNote}
            </div>
            <button
              type="button"
              onClick={onRebook}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-emerald-700 px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              <Ticket className="h-3.5 w-3.5" aria-hidden="true" /> See the cheaper fare
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (rebook.lockedReason) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" aria-hidden="true" /> {rebook.lockedReason}
      </div>
    );
  }

  // 2. Your fare held, but a DIFFERENT flight that day is cheaper. Say which
  //    one, and say it is a different flight — never call it a fare drop.
  if (alt && alt.worthSwitching) {
    return (
      <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <ArrowLeftRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-sky-900">
              A different flight that day is ${alt.savings} cheaper
            </div>
            <div className="mt-0.5 text-xs text-sky-800">
              Your fare has not moved. {describeAlternative(alt)} is ${alt.price} per person against
              the ${rebook.pricePaid} you paid — a different departure, same day. {rebook.creditNote}
            </div>
            <button
              type="button"
              onClick={onRebook}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-sky-700 px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              <Ticket className="h-3.5 w-3.5" aria-hidden="true" /> See that flight
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Nothing to act on. Say what we are actually watching, so silence is
  //    legible rather than mistaken for "we stopped looking".
  const held =
    rebook.currentFare != null && rebook.pricePaid != null && rebook.savings <= 0
      ? " · your fare is holding at $" + rebook.currentFare
      : rebook.savings > 0
        ? ` · $${rebook.savings} cheaper so far (below the $${rebook.threshold} rebook bar)`
        : "";
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
      Watching the flight you booked
      {held}
    </div>
  );
}
